import React, { useEffect, useRef, useCallback } from 'react';
import { TimeOfDay, WeatherType } from '../types';

interface GardenShootingStarsProps {
  timeOfDay: TimeOfDay;
  weather: WeatherType;
  starsOpacity: number;
  nocheWeight?: number;
}

interface ShootingStarInstance {
  id: number;
  ang: number;
  dist: number;
  len: number;
  dur: number;
  top: number;
  left: number;
  opacity: number;
}

export const GardenShootingStars: React.FC<GardenShootingStarsProps> = React.memo(({
  timeOfDay,
  weather,
  starsOpacity,
  nocheWeight = 0,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const nextIdRef = useRef(1);

  // Checks whether the sky conditions allow shooting stars (like in para-ti-mvd.com)
  const isVisible =
    (timeOfDay === 'night' || timeOfDay === 'sunset' || starsOpacity > 0.25) &&
    weather !== 'rainy';

  // Spawns a shooting star directly into the DOM matching the exact para-ti-mvd.com implementation
  const spawnShootingStar = useCallback(
    (customAngle?: number, customTop?: number, customLeft?: number, delay = 0) => {
      if (!containerRef.current) return;

      setTimeout(() => {
        if (!containerRef.current) return;

        const ang = customAngle ?? 148 + Math.random() * 26; // 148deg - 174deg trajectory
        const dist = 55 + Math.random() * 45; // 55vw - 100vw travel distance
        const largo = 110 + Math.random() * 130; // 110px - 240px luminous tail
        const dur = 1.0 + Math.random() * 0.9; // 1.0s - 1.9s duration
        const topVh = customTop ?? 4 + Math.random() * 34; // 4vh - 38vh
        const leftVw = customLeft ?? 52 + Math.random() * 44; // 52vw - 96vw
        const opacity = Math.min(1, Math.max(0.65, (nocheWeight || starsOpacity) + 0.25));

        const starEl = document.createElement('div');
        starEl.className = 'shoot';
        starEl.style.setProperty('--ang', `${ang}deg`);
        starEl.style.setProperty('--dist', `${dist}vw`);
        starEl.style.setProperty('--len', `${largo}px`);
        starEl.style.setProperty('--dur', `${dur}s`);
        starEl.style.top = `${topVh}vh`;
        starEl.style.left = `${leftVw}vw`;
        starEl.style.opacity = `${opacity.toFixed(2)}`;

        const tailSpan = document.createElement('span');
        tailSpan.className = 'shoot-tail';
        const headSpan = document.createElement('span');
        headSpan.className = 'shoot-head';
        starEl.appendChild(tailSpan);
        starEl.appendChild(headSpan);

        containerRef.current.appendChild(starEl);

        // Auto remove element after animation completes
        setTimeout(() => {
          if (starEl.parentNode) {
            starEl.remove();
          }
        }, dur * 1000 + 400);
      }, delay);
    },
    [nocheWeight, starsOpacity]
  );

  // Periodic star showers (lluvia de estrellas) like para-ti-mvd.com
  useEffect(() => {
    if (!isVisible) return;

    let timeoutId: ReturnType<typeof setTimeout>;

    const triggerLluvia = () => {
      if (document.hidden || !isVisible) {
        timeoutId = setTimeout(triggerLluvia, 4000);
        return;
      }

      // Randomly spawn 1, 2, or 3 shooting stars in sequence
      const cantidad = Math.random() < 0.28 ? 2 + Math.floor(Math.random() * 2) : 1;
      for (let i = 0; i < cantidad; i++) {
        spawnShootingStar(undefined, undefined, undefined, i * (180 + Math.random() * 260));
      }

      // Schedule next meteor shower between 4.5s and 10s
      const nextDelay = 4500 + Math.random() * 6500;
      timeoutId = setTimeout(triggerLluvia, nextDelay);
    };

    // Initial shooting star after 1.5s
    timeoutId = setTimeout(triggerLluvia, 1500);

    return () => clearTimeout(timeoutId);
  }, [isVisible, spawnShootingStar]);

  if (!isVisible) return null;

  return (
    <div
      ref={containerRef}
      className="fx-stars"
      aria-hidden="true"
      id="para-ti-shooting-stars"
    />
  );
});

GardenShootingStars.displayName = 'GardenShootingStars';
